<template>
  <BModal :id="id" :visible="show" scrollable size="md" body-class="" :footer-class="'d-none'" header-class="bg-white border-0" @hide="$emit('hide')">
    <template #modal-header>
      <img
        :src="industry == 'cannabis' ? '/img/logos/apex-logo.png' : '/img/logos/bushel-logo.png'"
        :alt="industry == 'cannabis' ? 'Apex Logo' : 'Bushel44 Logo'"
        width="200px"
        class="ml-auto mr-auto mt-3"
      />
      <button type="button" class="modal-close" aria-label="Close" @click="$emit('hide')">
        <i class="fa fa-times-thin" style="font-size: 1.5em" />
      </button>
    </template>
    <div v-if="!successfullyLoggedIn">
      <div @keyup.enter="submitForm">
        <div class="container pb-1">
          <div v-if="!twoFactorEnabled">
            <transition name="bounce">
              <BushelTextInputMaterial
                :value="formData.email"
                type="email"
                name="email"
                :autofocus="true"
                :label-active="'Email'"
                :error="submitted && !validateEmail(formData.email)"
                autocomplete="email"
                :error-text="'Required'"
                @input="(data) => (formData.email = data)"
              />
            </transition>
            <transition name="bounce">
              <div v-if="!showPasswordReset" class="form-group material-form">
                <BushelTextInputMaterial
                  :value="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  :error="submitted && formData.password.length < 8"
                  :error-text="'8 char min, letters, mixedCase, numbers, symbols'"
                  :label-active="'Password'"
                  :append-icon="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
                  :message="'8 char min, letters, mixedCase, numbers, symbols'"
                  @input="(data) => (formData.password = data)"
                  @icon-clicked="showPassword = !showPassword"
                />
              </div>
            </transition>
          </div>
          <div v-else class="mb-2">
            <BushelTextInputMaterial
              :value="formData.token"
              type="text"
              :label-active="'Authy Token'"
              :error="tokenSubmitted && !formData.token"
              :error-text="'Required'"
              :message="'Please use the Authy App on your phone to login'"
              @input="(data) => (formData.token = data)"
            />
          </div>
          <div class="text-center mt-4">
            <button
              class="btn btn-success btn-block"
              dusk="signin-button"
              :disabled="(resetSubmitted && showPasswordReset) || submitting"
              @click="submitForm"
            >
              {{ !showPasswordReset ? "Sign In" : "Reset Password" }}
              <i v-if="submitting" class="fas fa-circle-notch fa-spin" />
            </button>
            <transition name="fade">
              <div v-if="resetSubmitted && showPasswordReset && !submitting" class="alert alert-info mt-3 text-center">
                <h5>Reset Password Email Sent</h5>
                <div>Please check your email for a link to reset your password. If you don't see it, check your spam folder.</div>
              </div>
            </transition>
          </div>
          <div v-if="buyerData && buyerData.buyer_company && !showPasswordReset" class="alert alert-info mt-3 text-center">
            <h5>Don't have a login?</h5>
            <div v-if="buyerData.buyer_company.owner && buyerData.buyer_company.owner.name">
              Please contact your co-worker {{ buyerData.buyer_company.owner.name }} for an invite to {{ appName }}.
            </div>
            <div v-else>Please ask your co-workers for an invite to {{ appName }}.</div>
          </div>
          <div v-if="error && !showPasswordReset" class="alert alert-warning mt-1 mb-0">Invalid Login Information</div>
        </div>
        <div v-if="!twoFactorEnabled">
          <BushelBtn class="mt-3 btn-sm m-1" variant="secondary" ripple-color="grey" flat @click="showResetPassword">
            {{ showPasswordReset ? "Back" : "Reset Password" }}
          </BushelBtn>
        </div>
      </div>
    </div>
    <div v-else>
      <Checkmark :title="'Success'" subtext="Thanks! Redirecting you to the store now." />
    </div>
  </BModal>
</template>

<script>
import { mapActions } from "vuex";
import * as api from "@/services/api";
import { reloadScreen, navTo, validateEmail } from "@/services/helpers";
import BushelTextInputMaterial from "@/components/common/forms/BushelTextInputMaterial.vue";
import Checkmark from "@/components/common/Checkmark.vue";

export default {
  components: { BushelTextInputMaterial, Checkmark },

  props: {
    show: { type: Boolean, required: true },
    id: { type: String, default: "sneakyLoginModal" },
    buyerUuid: { type: String, required: true },
    buyerData: { type: Object, required: true },
    invitationEmail: { type: String, required: true },
    noReloadAfterAuth: { type: Boolean, default: false },
  },
  emits: ["hide"],
  data() {
    return {
      appName: APP_NAME,
      industry: APP_INDUSTRY,
      showPasswordReset: false,
      showPassword: false,
      submitted: false,
      submitting: false,
      resetSubmitted: false,
      error: false,
      twoFactorEnabled: false,
      tokenSubmitted: false,
      successfullyLoggedIn: false,
      workingOnLogin: false,
      formData: {
        email: this.invitationEmail ? this.invitationEmail : null,
        password: "",
        token: null,
      },
    };
  },
  computed: {},

  mounted() {},

  methods: {
    navTo,
    ...mapActions({
      loginAction: "auth/login",
      fetchSelf: "auth/fetchSelf",
    }),
    validateEmail,
    resetPassword() {
      if (this.validateEmail(this.formData.email)) {
        this.submitting = true;
        this.resetSubmitted = true;
        this.error = false;
        api.post("/auth/password/resetEmail", { email: this.formData.email }).then((response) => {
          this.submitting = false;
        });
      }
    },
    showResetPassword() {
      this.showPasswordReset = !this.showPasswordReset;
    },
    submitForm() {
      if (!this.submitting) {
        this.showPasswordReset ? this.resetPassword() : this.signIn();
      }
    },
    signIn() {
      this.submitted = true;
      this.error = false;
      if (this.validateEmail(this.formData.email) && this.formData.password.length >= 8) {
        this.submitting = true;
        const payload = { email: this.formData.email, token: this.formData.token, password: this.formData.password };
        this.loginAction(payload).then((response) => {
          if (response == "success") {
            this.successfullyLoggedIn = true;
            if (this.noReloadAfterAuth) {
              this.$emit("hide");
            } else {
              reloadScreen();
            }
            // const that = this;
            // setTimeout(() => {
            //   that.$emit('hide');
            // }, 2000);
          } else if (response === "two factor enabled") {
            this.twoFactorEnabled = true;
            this.submitting = false;
          } else {
            this.error = true;
            this.submitting = false;
          }
        });
      }
    },
  },
};
</script>
